import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { SEO } from "./components/SEO";
import { Header } from "./components/Header";
import { Footer } from "./components/Footer";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { LoadingSpinner } from "./components/LoadingSpinner";
import { toast } from "sonner";
import { cleanPath, isValidPath } from "@/utils/canonicalUrls";

const Index = lazy(() => import("./pages/Index"));
const KeywordAnalyse = lazy(() => import("./pages/KeywordAnalyse"));
const BacklinkProfil = lazy(() => import("./pages/BacklinkProfil"));
const ContentOptimierung = lazy(() => import("./pages/ContentOptimierung"));
const SerpSnippet = lazy(() => import("./pages/SerpSnippet"));
const LinkOptimizer = lazy(() => import("./pages/LinkOptimizer"));
const LokaleSeo = lazy(() => import("./pages/LokaleSeo"));
const PerformanceCheck = lazy(() => import("./pages/PerformanceCheck"));
const MobileSeo = lazy(() => import("./pages/MobileSeo"));
const Knowledge = lazy(() => import("./pages/Knowledge"));
const AboutUs = lazy(() => import("./pages/AboutUs"));
const Contact = lazy(() => import("./pages/Contact"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Auth = lazy(() => import("./pages/Auth"));
const Admin = lazy(() => import("./pages/Admin"));
const NotFound = lazy(() => import("./pages/NotFound"));

function PathHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Skip processing for the root path
    if (currentPath === '/') return;
    
    // Aggressively clean the path
    const cleanedPath = cleanPath(currentPath);
    
    // Log for debugging
    console.log('Current path:', currentPath);
    console.log('Cleaned path:', cleanedPath);
    
    // Always redirect if paths don't match exactly
    if (cleanedPath !== currentPath) {
      console.log('Redirecting to cleaned path');
      navigate(cleanedPath, { replace: true });
      return;
    }
    
    // Validate the cleaned path
    if (!isValidPath(cleanedPath)) {
      console.log('Invalid path, redirecting to 404');
      navigate('/404', { replace: true });
      return;
    }
  }, [location.pathname, navigate]);
  
  return null;
}

function App() {
  return (
    <Router>
      <ErrorBoundary>
        <SEO />
        <div className="min-h-screen flex flex-col">
          <Header />
          <main className="flex-grow">
            <PathHandler />
            <Suspense fallback={<LoadingSpinner />}>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/keyword-analyse" element={<KeywordAnalyse />} />
                <Route path="/backlink-profil" element={<BacklinkProfil />} />
                <Route path="/content-optimierung" element={<ContentOptimierung />} />
                <Route path="/serp-snippet" element={<SerpSnippet />} />
                <Route path="/link-optimierer" element={<LinkOptimizer />} />
                <Route path="/lokale-seo" element={<LokaleSeo />} />
                <Route path="/performance-check" element={<PerformanceCheck />} />
                <Route path="/mobile-seo" element={<MobileSeo />} />
                <Route path="/knowledge/*" element={<Knowledge />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/404" element={<NotFound />} />
                <Route path="*" element={<Navigate to="/404" replace />} />
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </ErrorBoundary>
    </Router>
  );
}

export default App;